import React, { useCallback, useState } from "react";
import { Event, NotificationType, TicketV2 } from "@markit/common.types";
import { Colors } from "../../utils/colors";
import ListTabs from "../DropdownsAndTabs/ListTabs";
import GridTableHeader from "../Headers/GridTableHeader";
import StandardListContainerBox from "../Containers/StandardListContainerBox";
import { Virtuoso } from "react-virtuoso";
import type { ReactNode } from "react";
import BlackHoverButton from "../Buttons/BlackHoverButton";
import { exportEventData } from "../../utils/eventUtils/formUtils";
import { showNotificationBanner } from "../../utils/notificationUtils";
import { useDispatch } from "react-redux";
import AlertContainer from "../Containers/AlertContainer";
import { Icon } from "@iconify/react";
import ScanningInstructionsModal from "./DashboardModals/ScanningInstructionsModal";
import AddSingleContactModal from "../CreatorDashboard/CreatorProfile/ImportContacts/AddSingleContactModal";
import SearchBoxContactsContainer, {
  SearchBoxContactsMode,
} from "../Containers/SearchBox/SearchBoxContactsContainer";
import {
  isEventRelatedType,
  isProductSpecificType,
} from "@markit/common.utils";

export type DashboardListPanelConfig = {
  listValue: number;
  setListValue: (index: number) => void;
  /** Tab labels (e.g. ["Attendees", "Organizers", "Refunded"] or ["Orders", "Refunded"]) */
  tabLabels: string[];
  /** Counts per tab; use -1 for loading */
  tabNumbers: number[];
  /** If true, show single "Submissions (n)" header instead of tabs */
  useSubmissionsHeader?: boolean;
  submissionsCount?: number;
  searchTerm: string;
  onSearch: (text: string) => void;
  searchMode: SearchBoxContactsMode;
  setSearchMode: (searchMode: SearchBoxContactsMode) => void;
  gridFlexSections: number[];
  gridSectionTitles: string[];
};

export type DashboardListPanelProps = {
  config: DashboardListPanelConfig;
  event: Event;
  dashboardVisiblity: number;
  tickets: TicketV2[];
  renderItem: (ticket: TicketV2) => ReactNode;
  onLoadMore: () => void;
  emptyState: ReactNode;
  listFooter: ReactNode;
  showScanButton: boolean;
  isLoading?: boolean;
};

/**
 * Used within GuestListPanel and OrderListPanel to display the list of tickets/orders
 */
export const DashboardListPanel = (props: DashboardListPanelProps) => {
  const {
    config,
    event,
    dashboardVisiblity,
    tickets,
    renderItem,
    onLoadMore,
    emptyState,
    listFooter,
    showScanButton,
    isLoading = false,
  } = props;
  const {
    listValue,
    setListValue,
    tabLabels,
    tabNumbers,
    useSubmissionsHeader,
    submissionsCount = 0,
    searchTerm,
    onSearch,
    searchMode,
    setSearchMode,
    gridFlexSections,
    gridSectionTitles,
  } = config;
  const dispatch = useDispatch();
  const [alertText, setAlertText] = useState({ heading: "", subHeading: "" });
  const [isDownloading, setDownloading] = useState(false);
  const [isScanningInstructionsModalOpen, setIsScanningInstructionsModalOpen] =
    useState(false);
  const [addGuestVisible, setAddGuestVisible] = useState(false);

  const styles = {
    guestListComponent: { paddingInline: 14, paddingBottom: 12 },
    submissionsHeader: {
      fontSize: 14,
      fontWeight: 500,
      paddingInline: 10,
    },
    listHeaderButton: {
      paddingInline: 14,
      paddingBlock: 12,
      border: `0.5px solid ${Colors.GRAY2}`,
      whiteSpace: "nowrap",
    },
  };

  const onExportFinish = useCallback(
    (success: boolean) => {
      if (success) {
        if (alertText.heading !== "Export Failed") {
          showNotificationBanner(
            dispatch,
            "Data Exported",
            NotificationType.AFFIRMATIVE
          );
        }
      } else {
        setAlertText({
          heading: "Export Failed",
          subHeading: "Please try again or contact our hotline.",
        });
      }
    },
    [alertText.heading, dispatch]
  );

  const exportEventOnClick = useCallback(async () => {
    if (dashboardVisiblity >= 2) {
      await exportEventData(
        event,
        isDownloading,
        setDownloading,
        onExportFinish
      );
    } else {
      setAlertText({
        heading: "Not allowed to export data in this role",
        subHeading: "",
      });
    }
  }, [dashboardVisiblity, event, isDownloading, onExportFinish]);

  const EmptyPlaceholder = useCallback(
    () => React.createElement(React.Fragment, null, emptyState),
    [emptyState]
  );
  const Footer = useCallback(
    () => React.createElement(React.Fragment, null, listFooter),
    [listFooter]
  );

  const listComp = (
    <Virtuoso
      style={{ height: "100%" }}
      data={tickets}
      endReached={onLoadMore}
      increaseViewportBy={200}
      itemContent={(_index, item) => renderItem(item)}
      components={{ Footer, EmptyPlaceholder }}
    />
  );

  return (
    <>
      {isScanningInstructionsModalOpen ? (
        <ScanningInstructionsModal
          setIsScanningInstructionsModalOpen={
            setIsScanningInstructionsModalOpen
          }
        />
      ) : null}
      <StandardListContainerBox
        searchComp={
          <SearchBoxContactsContainer
            searchTerm={searchTerm}
            handleSearch={onSearch}
            searchMode={searchMode}
            setSearchMode={setSearchMode}
          />
        }
        headerRightComp={
          <div className="AlignedRow" style={{ gap: 7 }}>
            {showScanButton ? (
              <BlackHoverButton
                title={"Scan"}
                iconName="ion:scan"
                iconSize={14}
                onPress={() => setIsScanningInstructionsModalOpen(true)}
                containerStyles={styles.listHeaderButton}
                altBackgroundColor={Colors.WHITE1}
              />
            ) : null}
            <BlackHoverButton
              title="Export&nbsp;Data"
              iconName="ion:arrow-down"
              iconSize={14}
              onPress={exportEventOnClick}
              containerStyles={styles.listHeaderButton}
              iconLoading={isDownloading}
              altBackgroundColor={Colors.WHITE1}
            />
            <BlackHoverButton
              title={
                isEventRelatedType(event.eventType)
                  ? "Add Guest"
                  : isProductSpecificType(event.eventType)
                  ? "Add Order"
                  : "Add Submission"
              }
              iconName="ion:add-circle"
              iconSize={14}
              onPress={() => setAddGuestVisible(true)}
              containerStyles={styles.listHeaderButton}
              altBackgroundColor={Colors.WHITE1}
            />
          </div>
        }
        subHeaderComp={
          useSubmissionsHeader ? (
            <div
              style={{ paddingTop: 6, paddingBottom: 18, paddingInline: 10 }}
            >
              <span style={styles.submissionsHeader}>
                Submissions ({submissionsCount})
              </span>
            </div>
          ) : (
            <ListTabs
              tabLabels={tabLabels}
              tabNumbers={tabNumbers}
              selectedValue={listValue}
              onChange={setListValue}
              containerStyles={styles.guestListComponent}
              altColor={Colors.BLACK}
            />
          )
        }
        gridHeaderComp={
          <GridTableHeader
            flexSections={gridFlexSections}
            sectionTitles={gridSectionTitles}
          />
        }
        listComp={listComp}
        isLoading={isLoading}
      />
      {addGuestVisible ? (
        <AddSingleContactModal
          setIsVisible={() => setAddGuestVisible(false)}
          event={event}
        />
      ) : null}
      <AlertContainer
        headerComp={
          <div className="ColumnNormal" style={{ gap: 7 }}>
            <Icon
              icon={"ion:close-circle"}
              style={{ color: Colors.RED3 }}
              height={30}
            />
            {alertText.heading}
          </div>
        }
        subHeaderComp={
          alertText.subHeading !== "" ? alertText.subHeading : undefined
        }
        closeModal={() => setAlertText({ heading: "", subHeading: "" })}
        hideModal={alertText.heading === "" && alertText.subHeading === ""}
        altButtonLabel={
          alertText.heading === "Export Failed" ? "Got it" : undefined
        }
      />
    </>
  );
};
